import { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { NewPageTitleBar } from '@/components';
import { useApiStore } from '@core/store';
import { Upload, Assignment, Overview } from './components/fastlaner';
import { getMenuData } from '@core/data/menuData.ts';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import { eventBus, EventTypes } from '@core/pubsub/EventBus.ts';
import TabSelector, { TabItem } from '@/components/shared/TabSelector.tsx';
import { DateTime } from 'luxon';

const FastLaner: FCSecurePage = () => {
    const { t } = useTranslation();
    const { organisation, shopIds, loadOrganisation, loadShopIds } = useApiStore();
    const [activeTab, setActiveTab] = useState(0);
    const [fastLanerOrgId, setFastLanerOrgId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);

    const tabs: TabItem[] = [
        { key: 'upload', label: 'upload' },
        { key: 'assignment', label: 'assignment' },
        { key: 'overview', label: 'overview' }
    ];

    useEffect(() => {
        const initializeData = async () => {
            if (!organisation || !organisation.fastLanerOrgId) {
                await loadOrganisation();
            }
            if (!shopIds || shopIds.length === 0) {
                await loadShopIds();
            }
            setLastUpdate(DateTime.now());
        };
        initializeData();
    }, []);

    useEffect(() => {
        if (organisation?.fastLanerOrgId) {
            setFastLanerOrgId(organisation.fastLanerOrgId);
        }
    }, [organisation]);

    const handleRefresh = useCallback(async () => {
        eventBus.emit(EventTypes.WIDGETS_RELOAD);
        setLastUpdate(DateTime.now());
    }, []);

    useRegisterRefresh(handleRefresh, loading);

    const handleTabChange = useCallback((newValue: number) => {
        setActiveTab(newValue);
        setLastUpdate(DateTime.now());
    }, []);

    return (
        <Box>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-settings" />}
                title={t('fastLanerSettings')}
                menus={getMenuData()}
                iconSize="20px"
                currentRoute={{ path: '/manage-organisation/fastLanerSettings' }}
                t={t}
                lastUpdate={lastUpdate}
            />

            <Box sx={{ width: '100%', px: 3 }}>
                <TabSelector value={activeTab} onChange={handleTabChange} tabs={tabs}>
                    {activeTab == 0 && (
                        <Upload
                            fastLanerOrgId={fastLanerOrgId}
                            onLoading={() => setLoading(true)}
                            onLoaded={() => setLoading(false)}
                        />
                    )}
                    {activeTab == 1 && (
                        <Assignment
                            fastLanerOrgId={fastLanerOrgId}
                            shopIds={shopIds || []}
                            onLoading={() => setLoading(true)}
                            onLoaded={() => setLoading(false)}
                        />
                    )}
                    {activeTab == 2 && (
                        <Overview
                            shopIds={shopIds || []}
                            onLoading={() => setLoading(true)}
                            onLoaded={() => setLoading(false)}
                        />
                    )}
                </TabSelector>
            </Box>
        </Box>
    );
};

export default FastLaner;
